import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import ImageUploader from "@/components/ImageUploader";

const FONT_CHOICES = [
  "Playfair Display", "Cormorant Garamond", "Fraunces", "DM Serif Display",
  "Libre Caslon Text", "Prata", "Italiana", "Marcellus",
  "Montserrat", "Inter", "Poppins", "Work Sans", "Nunito Sans", "Jost", "Lato",
];

export default function AdminContent() {
  const [form, setForm] = useState({});
  const [products, setProducts] = useState([]);
  const [featuredIds, setFeaturedIds] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/content").then(({ data }) => {
      setForm(data || {});
      setFeaturedIds(data?.featured_product_ids || []);
    });
    api.get("/admin/products").then(({ data }) => setProducts(data.filter((p) => !p.is_archived)));
  }, []);

  const applyTheme = () => {
    window.dispatchEvent(new CustomEvent("duo:theme", { detail: form }));
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.put("/admin/content", { ...form, featured_product_ids: featuredIds });
      toast.success("Conteúdo atualizado");
      applyTheme();
    } catch (err) { toast.error("Erro ao salvar"); } finally { setSaving(false); }
  };

  const toggleFeatured = (id) => {
    setFeaturedIds((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
  };

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  return (
    <form onSubmit={save} className="space-y-12 max-w-3xl" data-testid="admin-content">
      <h1 className="font-display text-3xl text-[#49392F]">Conteúdo do site</h1>

      <Section title="Banner principal">
        <input className="duo-input" placeholder='Rótulo acima do título (ex.: "— Nova coleção")' value={form.hero_eyebrow || ""} onChange={(e) => update("hero_eyebrow", e.target.value)} data-testid="content-hero-eyebrow" />
        <input className="duo-input" placeholder="Título do banner" value={form.hero_title || ""} onChange={(e) => update("hero_title", e.target.value)} data-testid="content-hero-title" />
        <input className="duo-input" placeholder="Subtítulo" value={form.hero_subtitle || ""} onChange={(e) => update("hero_subtitle", e.target.value)} />
        <div>
          <div className="text-[11px] tracking-[0.3em] uppercase text-[#49392F]/80 mb-2">Imagem do banner</div>
          <ImageUploader value={form.hero_image ? [form.hero_image] : []} onChange={(v) => update("hero_image", v[0] || "")} max={1} />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <input className="duo-input" placeholder='Legenda sobre a imagem (ex.: "Edição Áurea")' value={form.hero_overlay_eyebrow || ""} onChange={(e) => update("hero_overlay_eyebrow", e.target.value)} />
          <input className="duo-input" placeholder='Chamada da legenda (ex.: "Delicadeza em cada detalhe")' value={form.hero_overlay_title || ""} onChange={(e) => update("hero_overlay_title", e.target.value)} />
        </div>
      </Section>

      <Section title="Sobre a marca & vídeo">
        <textarea rows={4} className="duo-input" placeholder="Texto institucional" value={form.about_text || ""} onChange={(e) => update("about_text", e.target.value)} />
        <div>
          <div className="text-[11px] tracking-[0.3em] uppercase text-[#49392F]/80 mb-2">Imagem "Sobre a marca" (seção lumière)</div>
          <ImageUploader value={form.about_image ? [form.about_image] : []} onChange={(v) => update("about_image", v[0] || "")} max={1} />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <input className="duo-input" placeholder='Título da seção do vídeo (ex.: "Conheça a Duo Áurea")' value={form.video_section_title || ""} onChange={(e) => update("video_section_title", e.target.value)} />
          <input className="duo-input" placeholder="Subtítulo da seção do vídeo" value={form.video_section_subtitle || ""} onChange={(e) => update("video_section_subtitle", e.target.value)} />
        </div>
        <input className="duo-input" placeholder="URL do vídeo (reel do Instagram ou YouTube)" value={form.video_url || ""} onChange={(e) => update("video_url", e.target.value)} data-testid="content-video-url" />
        <p className="text-[11px] text-[#49392F]/60">Reels do Instagram aparecem na vertical (9:16), vídeos do YouTube na horizontal (16:9). O vídeo é exibido inteiro, sem corte.</p>
      </Section>

      <Section title="Dados da empresa (recibo)">
        <input className="duo-input" placeholder="CNPJ (ex.: 69.669.703/0001-03)" value={form.brand_cnpj || ""} onChange={(e) => update("brand_cnpj", e.target.value)} data-testid="content-cnpj" />
        <p className="text-[11px] text-[#49392F]/60">Aparece no cabeçalho do recibo em PDF enviado às clientes.</p>
      </Section>

      <Section title="Redes sociais & contato">
        <input className="duo-input" placeholder="Instagram (URL completa)" value={form.instagram_url || ""} onChange={(e) => update("instagram_url", e.target.value)} />
        <input className="duo-input" placeholder="WhatsApp com DDI+DDD (ex.: 5511999999999)" value={form.whatsapp_number || ""} onChange={(e) => update("whatsapp_number", e.target.value)} data-testid="content-whatsapp" />
        <input className="duo-input" placeholder="E-mail de contato" value={form.email || ""} onChange={(e) => update("email", e.target.value)} />
      </Section>

      <Section title="Pix (pagamento imediato no pedido)">
        <input className="duo-input" placeholder="Chave Pix (CPF, CNPJ, e-mail, telefone ou aleatória)" value={form.pix_key || ""} onChange={(e) => update("pix_key", e.target.value)} data-testid="content-pix-key" />
        <div className="grid grid-cols-2 gap-3">
          <input className="duo-input" placeholder="Nome do recebedor (até 25 caracteres, sem acento)" value={form.pix_merchant_name || ""} onChange={(e) => update("pix_merchant_name", e.target.value)} data-testid="content-pix-name" />
          <input className="duo-input" placeholder="Cidade (até 15 caracteres, sem acento)" value={form.pix_merchant_city || ""} onChange={(e) => update("pix_merchant_city", e.target.value)} />
        </div>
        <p className="text-[11px] text-[#49392F]/60">O QR Code com o valor exato (produtos + frete) é gerado automaticamente na página de confirmação.</p>
      </Section>

      <Section title="Frete (Melhor Envio)">
        <input className="duo-input" placeholder="Token da API Melhor Envio" value={form.melhorenvio_token || ""} onChange={(e) => update("melhorenvio_token", e.target.value)} />
        <label className="inline-flex items-center gap-2 text-[13px] text-[#49392F]">
          <input type="checkbox" checked={!!form.melhorenvio_sandbox} onChange={(e) => update("melhorenvio_sandbox", e.target.checked)} />
          Usar ambiente de testes (sandbox)
        </label>
        <input className="duo-input" placeholder="CEP de origem (ex.: 01310-100)" value={form.melhorenvio_origin_cep || ""} onChange={(e) => update("melhorenvio_origin_cep", e.target.value)} />
        <div className="text-[11px] tracking-[0.3em] uppercase text-[#49392F]/80 mt-2">Embalagem padrão</div>
        <div className="grid grid-cols-4 gap-3">
          <input className="duo-input" type="number" step="0.1" placeholder="Altura (cm)" value={form.pkg_height ?? ""} onChange={(e) => update("pkg_height", parseFloat(e.target.value))} />
          <input className="duo-input" type="number" step="0.1" placeholder="Largura (cm)" value={form.pkg_width ?? ""} onChange={(e) => update("pkg_width", parseFloat(e.target.value))} />
          <input className="duo-input" type="number" step="0.1" placeholder="Comprimento (cm)" value={form.pkg_length ?? ""} onChange={(e) => update("pkg_length", parseFloat(e.target.value))} />
          <input className="duo-input" type="number" step="0.01" placeholder="Peso (kg)" value={form.pkg_weight ?? ""} onChange={(e) => update("pkg_weight", parseFloat(e.target.value))} />
        </div>
        <p className="text-[11px] text-[#49392F]/60">Sem token, o frete aparece como "a combinar" e é confirmado por WhatsApp.</p>
      </Section>

      <Section title="Aparência (cores e fontes)">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <ColorField label="Fundo" value={form.theme_background} onChange={(v) => update("theme_background", v)} />
          <ColorField label="Superfície" value={form.theme_surface} onChange={(v) => update("theme_surface", v)} />
          <ColorField label="Primária" value={form.theme_primary} onChange={(v) => update("theme_primary", v)} />
          <ColorField label="Destaque (ouro)" value={form.theme_accent} onChange={(v) => update("theme_accent", v)} />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <div className="text-[11px] tracking-[0.3em] uppercase text-[#49392F]/80 mb-2">Fonte dos títulos</div>
            <select className="duo-input" value={form.font_heading || "Playfair Display"} onChange={(e) => update("font_heading", e.target.value)}>
              {FONT_CHOICES.map((f) => <option key={f} value={f}>{f}</option>)}
            </select>
          </div>
          <div>
            <div className="text-[11px] tracking-[0.3em] uppercase text-[#49392F]/80 mb-2">Fonte do corpo</div>
            <select className="duo-input" value={form.font_body || "Montserrat"} onChange={(e) => update("font_body", e.target.value)}>
              {FONT_CHOICES.map((f) => <option key={f} value={f}>{f}</option>)}
            </select>
          </div>
        </div>
        <button type="button" onClick={applyTheme} className="duo-btn duo-btn-secondary mt-3">Visualizar sem salvar</button>
      </Section>

      <Section title="Destaques da home">
        <p className="text-[12px] text-[#49392F]/70">Marque os produtos que vão aparecer na seção "Mais desejadas".</p>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-2 max-h-80 overflow-y-auto pr-2">
          {products.map((p) => (
            <label key={p.id} className={`block p-3 border cursor-pointer ${featuredIds.includes(p.id) ? "border-[#49392F] bg-[#E8DCCB]/40" : "border-[#C2A064]/20"}`}>
              <input type="checkbox" checked={featuredIds.includes(p.id)} onChange={() => toggleFeatured(p.id)} className="mr-2" />
              <span className="text-[12px]">{p.name}</span>
            </label>
          ))}
        </div>
      </Section>

      <div className="sticky bottom-0 bg-[#F8F5EF] py-4 border-t border-[#C2A064]/20">
        <button disabled={saving} className="duo-btn duo-btn-primary" data-testid="content-save">
          {saving ? "Salvando..." : "Salvar alterações"}
        </button>
      </div>
    </form>
  );
}

function Section({ title, children }) {
  return (
    <section className="space-y-3">
      <h3 className="text-[11px] tracking-[0.3em] uppercase text-[#49392F]/80">{title}</h3>
      {children}
    </section>
  );
}

function ColorField({ label, value, onChange }) {
  return (
    <div>
      <div className="text-[11px] tracking-[0.2em] uppercase text-[#49392F]/70 mb-1">{label}</div>
      <div className="flex gap-2 items-center">
        <input type="color" value={value || "#000000"} onChange={(e) => onChange(e.target.value)} className="h-10 w-12 border border-[#E8DCCB] bg-white" />
        <input type="text" value={value || ""} onChange={(e) => onChange(e.target.value)} className="duo-input flex-1" placeholder="#000000" />
      </div>
    </div>
  );
}
